import { Box, HStack, Stack, Text } from '@chakra-ui/react'
import { DateTime } from 'luxon'
import { useRouter } from 'next/router'
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { isNotNull } from '@/utils/helpers'
import parseQueryNumber from '@/utils/parseQueryNumber'
import toEnumValueOrNull from '@/utils/toEnumValueOrNull'

import LearnerFeedbackQ from '@/queries/LearnerFeedbackQ'
import SessionQ from '@/queries/SessionQ'
import TopicQ from '@/queries/TopicQ'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { SQL } from '@/client/query'
import {
  captureFeedbackEvent,
  type FeedbackEventProps,
  newFeedbackFormSessionId,
} from '@/client/services/analytics/feedback'
import {
  DIALOGUES_REFLECTION_EXPIRES_AFTER_DAYS,
  QUERY_REFLECTION_SESSION_ID,
} from '@/client/services/dialogues/constants'
import useDialoguesReflectionFormInputs from '@/client/services/formInputs/useDialoguesReflectionFormInputs'
import {
  LEARNER_FEEDBACK_TYPE_DIALOGUES,
  LearnerFeedbackAttributeTag,
} from '@/client/services/learnerFeedback/constants'
import type { SubmitLearnerFeedbacksItem } from '@/client/services/learnerFeedback/types'
import { useTRPCClient } from '@/client/trpc'

import LearnerFeedbackDisplayFromQuery from '@/components/Dialogues/LearnerFeedbackDisplayFromQuery'
import Spinner from '@/components/Spinner'
import FormWizardV2 from '@/components/UI/FormWizardV2/FormWizardV2'
import type { FormWizardV2Values } from '@/components/UI/FormWizardV2/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

function isStringArray(val: unknown): val is string[] {
  return Array.isArray(val) && val.every(v => typeof v === 'string')
}

export default function DialoguesReflectionModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}): ReactElement | null {
  const query = useQuery()
  const router = useRouter()
  const me = useUser()

  const sessionId = parseQueryNumber({
    value: router.query[QUERY_REFLECTION_SESSION_ID],
    defaultValue: 0,
  })
  const session = query.one(SessionQ.where({ id: sessionId }))
  const sessionEndDateISO = session(SessionQ.getComputedEndDate())
  const topic = session.one(SessionQ.getTopic())
  const topicName = topic(TopicQ.name)
  const title = `Session Reflection${topicName != null ? `: ${topicName}` : ''}`

  const isExpired = useMemo(() => {
    if (sessionEndDateISO == null) {
      return false
    }

    return (
      DateTime.fromISO(sessionEndDateISO) <
      DateTime.now().minus({ days: DIALOGUES_REFLECTION_EXPIRES_AFTER_DAYS })
    )
  }, [sessionEndDateISO])

  const partnerCount = session == null ? null : session(SessionQ.countConversationPartners(me.id))

  const allPartners = useMemo(
    () => session.many(SessionQ.getConversationPartners(me.id)),
    [session, me.id]
  )

  const selfFeedbacks =
    session?.many(
      LearnerFeedbackQ.where(
        {
          sessionId: SessionQ.id,
          reviewerUserId: me.id,
          type: LEARNER_FEEDBACK_TYPE_DIALOGUES,
        },
        SQL`${LearnerFeedbackQ.selfReflectionFilter(true)}`
      )
    ) ?? []

  const peerFeedbacks =
    session?.many(
      LearnerFeedbackQ.where(
        {
          sessionId: SessionQ.id,
          reviewerUserId: me.id,
          type: LEARNER_FEEDBACK_TYPE_DIALOGUES,
        },
        SQL`${LearnerFeedbackQ.selfReflectionFilter(false)}`
      )
    ) ?? []

  const dialoguesFeedbacks = [...selfFeedbacks, ...peerFeedbacks]

  const userHasCompletedReflection = dialoguesFeedbacks.length > 0

  // Modal stays mounted while isOpen toggles. We reset tracking state after a
  // close so the next open is treated as a fresh fill attempt with its own
  // form_session_id. Resetting on close (rather than on reopen) means the new
  // id is already in state when the next render's effects run, so viewed /
  // section / field events all see the same id with no setState races.
  const [formSessionId, setFormSessionId] = useState(newFeedbackFormSessionId)
  const trpcClient = useTRPCClient()
  const submittedRef = useRef(false)
  const viewedRef = useRef(false)
  const feedbackProps: FeedbackEventProps = useMemo(
    () => ({
      form_key: 'dialogues_session_reflection',
      form_session_id: formSessionId,
      user_id: me.id,
    }),
    [formSessionId, me.id]
  )
  const feedbackPropsRef = useRef(feedbackProps)
  feedbackPropsRef.current = feedbackProps

  useEffect(() => {
    if (!isOpen || sessionId === 0 || viewedRef.current) {
      return
    }
    viewedRef.current = true
    captureFeedbackEvent('feedback_form_viewed', feedbackPropsRef.current)
  }, [isOpen, sessionId])

  useEffect(() => {
    if (!isOpen) {
      return
    }
    const handleUnload = () => {
      if (!submittedRef.current) {
        captureFeedbackEvent('feedback_form_dismissed', feedbackPropsRef.current)
      }
    }
    window.addEventListener('beforeunload', handleUnload)
    return () => window.removeEventListener('beforeunload', handleUnload)
  }, [isOpen])

  const handleClose = useCallback(() => {
    if (viewedRef.current && !submittedRef.current) {
      captureFeedbackEvent('feedback_form_dismissed', feedbackProps)
    }
    // Reset state so the next open is a fresh fill attempt.
    submittedRef.current = false
    viewedRef.current = false
    setFormSessionId(newFeedbackFormSessionId())
    onClose()
  }, [feedbackProps, onClose])

  const { steps } = useDialoguesReflectionFormInputs({
    allPartners,
    feedbackProps,
  })

  const handleSubmit = useCallback(
    async (values: FormWizardV2Values<string>) => {
      const peerItems = allPartners
        .map(partner => {
          const id = partner(UserQ.id) ?? 0
          if (id === 0) {
            return null
          }
          const stepValues = values[`peer-${id}`] ?? {}
          const rawTagsValue = stepValues[`peer-${id}-tags`]
          const rawTags = isStringArray(rawTagsValue) ? rawTagsValue : []
          const attributeTags = rawTags
            .map(tag => toEnumValueOrNull(LearnerFeedbackAttributeTag, tag))
            .filter(isNotNull)
          const commentValue = stepValues[`peer-${id}-comment`]
          const comment = typeof commentValue === 'string' ? commentValue.trim() : ''
          const hasContent = comment.length > 0 || attributeTags.length > 0
          return hasContent
            ? ({
                sessionId,
                revieweeUserId: id,
                comment,
                attributeTags,
                type: LEARNER_FEEDBACK_TYPE_DIALOGUES,
              } satisfies SubmitLearnerFeedbacksItem)
            : null
        })
        .filter(isNotNull)

      const items: SubmitLearnerFeedbacksItem[] = [...peerItems]

      const selfValues = values['self'] ?? {}
      const selfCommentValue = selfValues['self-comment']
      const selfComment = typeof selfCommentValue === 'string' ? selfCommentValue.trim() : ''
      if (selfComment.length > 0) {
        items.push({
          sessionId,
          revieweeUserId: me.id,
          comment: selfComment,
          attributeTags: [],
          type: LEARNER_FEEDBACK_TYPE_DIALOGUES,
        })
      }

      if (items.length > 0) {
        await trpcClient.series.submitLearnerFeedbacks.mutate(items)
      }

      submittedRef.current = true
      captureFeedbackEvent('feedback_form_submitted', feedbackProps)
      onClose()
    },
    [allPartners, me.id, onClose, sessionId, feedbackProps]
  )

  if (!isOpen || (isExpired && !userHasCompletedReflection) || sessionId === 0) {
    return null
  }

  return (
    <ModalV3
      open={isOpen}
      onClose={handleClose}
      size="3xl"
      disableClickOutside
      title={title}
      body={() => {
        if (userHasCompletedReflection) {
          return (
            <Box maxHeight="60vh" overflowY="auto" pr="8px">
              <Stack spacing="16px">
                <Text fontSize="md">Thank you for completing the reflection for this session!</Text>
                {dialoguesFeedbacks.map(dialoguesFeedback => (
                  <LearnerFeedbackDisplayFromQuery
                    key={`${dialoguesFeedback(LearnerFeedbackQ.id)}`}
                    feedback={dialoguesFeedback}
                    showRecipient
                    showType
                  />
                ))}
              </Stack>
            </Box>
          )
        }

        if (partnerCount == null) {
          return (
            <HStack>
              <Spinner />
              <Text>Finding conversation partners...</Text>
            </HStack>
          )
        }

        if (partnerCount === 0) {
          return <Text>No conversation partners found for this session</Text>
        }

        return (
          <FormWizardV2
            name="dialogues-reflection"
            checklistType="horizontal-stepper"
            steps={steps}
            maxWidth="100%"
            onSubmit={handleSubmit}
          />
        )
      }}
    />
  )
}
